import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState, useRef } from 'react';
import { oportunidadService, Oportunidad, PipelineEtapa } from '../../services/oportunidadService';

interface OportunidadCardProps {
  oportunidad: Oportunidad;
  onClick: () => void;
  isDraggingGlobal: boolean;
}

const OportunidadCard = ({ oportunidad, onClick, isDraggingGlobal }: OportunidadCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: oportunidad.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Separar el drag del click
  const { onPointerDown, onKeyDown } = listeners;
  const clickStartTime = useRef<number | null>(null);
  const hasMoved = useRef(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onPointerDown={(e) => {
        clickStartTime.current = Date.now();
        hasMoved.current = false;
        onPointerDown?.(e);
      }}
      onKeyDown={onKeyDown}
      onPointerMove={() => {
        hasMoved.current = true;
      }}
      onClick={(e) => {
        // Solo hacer click si no se está arrastrando, no se movió el mouse, y fue un click rápido
        const clickDuration = clickStartTime.current ? Date.now() - clickStartTime.current : 0;
        if (!isDragging && !isDraggingGlobal && !hasMoved.current && clickDuration < 300) {
          onClick();
        }
        clickStartTime.current = null;
        hasMoved.current = false;
      }}
      className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition"
      title="Click para editar, arrastra para mover"
    >
      <div className="mb-2">
        <h3 className="font-semibold text-gray-900">{oportunidad.nombre}</h3>
        {oportunidad.cliente && (
          <p className="text-sm text-gray-500">{oportunidad.cliente.nombre}</p>
        )}
      </div>
      <div className="mb-2">
        <p className="text-lg font-bold text-green-600">
          ${(typeof oportunidad.valor === 'string' ? parseFloat(oportunidad.valor) : (oportunidad.valor || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        <p className="text-xs text-gray-400">
          {oportunidad.probabilidad}% probabilidad
        </p>
      </div>
      {oportunidad.fechaCierreEstimada && (
        <p className="text-xs text-gray-500">
          Cierre: {new Date(oportunidad.fechaCierreEstimada).toLocaleDateString('es-MX')}
        </p>
      )}
      {oportunidad.usuarioAsignado && (
        <p className="text-xs text-gray-400 mt-2">
          {oportunidad.usuarioAsignado.name}
        </p>
      )}
    </div>
  );
};

interface EtapaColumnProps {
  etapa: PipelineEtapa;
  oportunidades: Oportunidad[];
  onOportunidadClick: (id: number) => void;
  isDraggingGlobal: boolean;
}

const EtapaColumn = ({ etapa, oportunidades, onOportunidadClick, isDraggingGlobal }: EtapaColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: etapa.id.toString(),
  });

  const getValorTotal = (ops: Oportunidad[]) => {
    return ops.reduce((sum, o) => {
      const valor = typeof o.valor === 'string' ? parseFloat(o.valor) : (o.valor || 0);
      return sum + valor;
    }, 0);
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-80 bg-gray-50 rounded-lg p-4 transition-colors ${isOver ? 'bg-blue-100' : ''}`}
      style={{ minHeight: '600px' }}
    >
      <div
        className="mb-4 p-3 rounded-lg text-white font-semibold"
        style={{ backgroundColor: etapa.color || '#3B82F6' }}
      >
        <div className="flex justify-between items-center">
          <span>{etapa.nombre}</span>
          <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm">
            {oportunidades.length}
          </span>
        </div>
        <div className="mt-2 text-sm opacity-90">
          ${getValorTotal(oportunidades).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>

      <SortableContext
        items={oportunidades.map(o => o.id.toString())}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {oportunidades.map((oportunidad) => (
            <OportunidadCard
              key={oportunidad.id}
              oportunidad={oportunidad}
              onClick={() => onOportunidadClick(oportunidad.id)}
              isDraggingGlobal={isDraggingGlobal}
            />
          ))}
          {oportunidades.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No hay oportunidades en esta etapa
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

const OportunidadesPipeline = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [oportunidadesPorEtapa, setOportunidadesPorEtapa] = useState<Map<number, Oportunidad[]>>(new Map());
  const [isDragging, setIsDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Requiere mover 10px antes de activar el drag
        delay: 200, // Delay de 200ms antes de activar el drag (click sostenido)
        tolerance: 5, // Tolerancia de 5px
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: etapasData } = useQuery(
    'pipeline-etapas',
    () => oportunidadService.getPipelineEtapas()
  );

  const { data: oportunidadesData, isLoading } = useQuery(
    'oportunidades-pipeline',
    () => oportunidadService.getAll(1, 1000, undefined, 'activa'),
    {
      onSuccess: (data) => {
        const oportunidades = data.data.oportunidades || [];
        const etapas = etapasData?.data.etapas || [];
        
        // Agrupar oportunidades por etapa
        const grouped = new Map<number, Oportunidad[]>();
        etapas.forEach(etapa => {
          grouped.set(etapa.id, oportunidades.filter(o => o.etapaId === etapa.id));
        });
        setOportunidadesPorEtapa(grouped);
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => oportunidadService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oportunidades-pipeline');
        queryClient.invalidateQueries('oportunidades');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar oportunidad');
        // Revertir cambios en caso de error
        queryClient.invalidateQueries('oportunidades-pipeline');
      }
    }
  );

  const etapas = etapasData?.data.etapas || [];
  const oportunidades = oportunidadesData?.data.oportunidades || [];

  // Sincronizar estado local con datos del servidor
  if (oportunidadesPorEtapa.size === 0 && etapas.length > 0 && oportunidades.length > 0) {
    const grouped = new Map<number, Oportunidad[]>();
    etapas.forEach(etapa => {
      grouped.set(etapa.id, oportunidades.filter(o => o.etapaId === etapa.id));
    });
    setOportunidadesPorEtapa(grouped);
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);

    if (!over) return;

    const oportunidadId = parseInt(active.id as string);
    let nuevaEtapaId: number;

    // Verificar si se arrastró sobre una etapa (columna) o sobre otra tarjeta
    const etapaEncontrada = etapas.find(e => e.id.toString() === over.id.toString());
    if (etapaEncontrada) {
      // Se arrastró directamente sobre la columna
      nuevaEtapaId = etapaEncontrada.id;
    } else {
      // Se arrastró sobre otra tarjeta, encontrar a qué etapa pertenece
      const tarjetaSobre = oportunidades.find(o => o.id.toString() === over.id.toString());
      if (!tarjetaSobre) return;
      nuevaEtapaId = tarjetaSobre.etapaId;
    }

    // Encontrar la oportunidad actual
    const oportunidad = oportunidades.find(o => o.id === oportunidadId);
    if (!oportunidad) return;

    // Si se mueve a la misma etapa, no hacer nada
    if (oportunidad.etapaId === nuevaEtapaId) return;

    // Actualizar estado local optimista
    const newMap = new Map(oportunidadesPorEtapa);
    
    // Remover de la etapa anterior
    const etapaAnterior = newMap.get(oportunidad.etapaId) || [];
    newMap.set(oportunidad.etapaId, etapaAnterior.filter(o => o.id !== oportunidadId));
    
    // Agregar a la nueva etapa
    const etapaNueva = newMap.get(nuevaEtapaId) || [];
    const oportunidadActualizada = { ...oportunidad, etapaId: nuevaEtapaId };
    newMap.set(nuevaEtapaId, [...etapaNueva, oportunidadActualizada]);
    
    setOportunidadesPorEtapa(newMap);

    // Actualizar en el servidor
    updateMutation.mutate({
      id: oportunidadId,
      data: { etapaId: nuevaEtapaId }
    });
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const oportunidadId = parseInt(active.id as string);
    const sobreEtapaId = parseInt(over.id as string);

    // Si se arrastra sobre una etapa diferente, actualizar visualmente
    const oportunidad = oportunidades.find(o => o.id === oportunidadId);
    if (!oportunidad || oportunidad.etapaId === sobreEtapaId) return;
  };

  const getActiveOportunidad = () => {
    if (!activeId) return null;
    return oportunidades.find(o => o.id.toString() === activeId);
  };

  const activeOportunidad = getActiveOportunidad();

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Pipeline de Oportunidades</h1>
          <p className="text-gray-600 mt-1">Arrastra las oportunidades entre etapas</p>
        </div>
        <button
          onClick={() => navigate('/crm/oportunidades/nueva')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          + Nueva Oportunidad
        </button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center">
          <p className="text-gray-500">Cargando pipeline...</p>
        </div>
      ) : etapas.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <p className="text-gray-500">No hay etapas configuradas en el pipeline</p>
          <p className="text-sm text-gray-400 mt-2">Contacta al administrador para configurar las etapas</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <div className="flex space-x-4 overflow-x-auto pb-4" style={{ minHeight: '600px' }}>
            {etapas.map((etapa) => (
              <EtapaColumn
                key={etapa.id}
                etapa={etapa}
                oportunidades={oportunidadesPorEtapa.get(etapa.id) || []}
                onOportunidadClick={(id) => navigate(`/crm/oportunidades/${id}`)}
                isDraggingGlobal={isDragging}
              />
            ))}
          </div>
          <DragOverlay>
            {activeOportunidad ? (
              <div className="bg-white rounded-lg shadow-lg p-4 w-80 opacity-90">
                <div className="mb-2">
                  <h3 className="font-semibold text-gray-900">{activeOportunidad.nombre}</h3>
                  {activeOportunidad.cliente && (
                    <p className="text-sm text-gray-500">{activeOportunidad.cliente.nombre}</p>
                  )}
                </div>
                <div className="mb-2">
                  <p className="text-lg font-bold text-green-600">
                    ${(typeof activeOportunidad.valor === 'string' ? parseFloat(activeOportunidad.valor) : (activeOportunidad.valor || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default OportunidadesPipeline;
